import { Build, Component, Element, h, Host, Method, Prop, State } from '@stencil/core';
import stringTemplate from './subcomponents/string-template';
import uid from '../../capabilities/identity/uid';
import warn from '../../capabilities/warn';
import {
  SortDirection,
  ThSortClass,
  DataAttributeTh,
  SortType,
} from './subcomponents/HpTableTypes';
import { HpTableFilterList } from './subcomponents/HpTableFilterList';
import { Hp6TableActionsType } from './subcomponents/HpTableActionEvent';

/**
 * @description Used for tabular data — that is, information presented in a
 * two-dimensional table comprised of rows and columns of cells containing data.
 * @inwork
 * @since 5.10.0
 * @slot - `default slot`: A fully qualified HTML `<table />` element, but does require full syntax,
 * ie, you must use colgroup, thead, tbody tags. To get row headers, use `th` as the first cell in a `tr`.
 * @version figma-v2
 */
@Component({
  tag: 'hp6-table',
  styleUrl: 'table.scss',
  shadow: false, // Satisfies single-tag policy since table is in a default slot as a direct child of the component
})
export class Table {
  /**
   * If set true, this table becomes filterable
   */
  @Prop() isFilterable: boolean = false;

  /**
   * If set true, this table becomes sortable
   */
  @Prop() isSortable: boolean = false;

  /**
   * Text used inside table reset button
   */
  @Prop() textLabelResetButton: string = 'Clear filters and sorting';

  /**
   * The label to the left of the <hp6-text-select /> used to sort
   */
  @Prop() textSelectLabel: string = 'Sort by';

  /**
   * The label of the first <option /> in the <hp6-text-select /> used to sort
   */
  @Prop() textSelectFirstOptionLabel: string = 'Select';

  /**
   * The contents of the <div id="{this.ariaDescribedbyValue}" /> that describes
   * this table. Used to describe table when table is NOT paginated
   *
   * Replacement values include:
   * - {numberOfRows} the total number or <tr />'s in the unpaginated <table />
   */
  @Prop() textTableDescription: string = 'Showing {numberOfRows} items';

  /**
   * The contents of the <div id="{this.ariaDescribedbyValue}" /> that describes
   * this table when pagination is being used. NOTE: This value is not read by
   * a screen reader, this.textTableDescriptionPaginationScreenReader is. This
   * value is aria-hidden="true". Used to describe table when table is paginated.
   *
   * Replacement values include:
   * - {numberOfRows} the total number or <tr />'s in the unpaginated <table />
   * - {paginationItemFirst} the index+1 of the first item being displayed
   *      in the paginated table
   * - {paginationItemLast} the index+1 of the last item being displayed
   *      in the paginated table
   */
  @Prop() textTableDescriptionPagination: string
    = 'Showing items {paginationItemFirst}-{paginationItemLast} of {numberOfRows}';

  /**
   * Same purpose as this.textTableDescriptionPagination and is ready by a
   * screen reader.
   */
  @Prop() textTableDescriptionPaginationScreenReader: string
    = 'Showing items {paginationItemFirst} through {paginationItemLast} of {numberOfRows}.';

  /**
   * Read by screen reader to describe how table is sorted.
   *
   * Not displayed on the screen.
   */
  @Prop() textTableSortingScreenReader: string
    = 'Sorting table by column {sortColumnNumber}, {innerHTML} {textSortDirection}.';

  /**
   * Read by screen reader to indicate which page is currently being viewed.
   *
   * Not displayed on the screen.
   */
  @Prop() textPaginationCurrentPageScreenReader: string
    = 'Currently on page {paginationCurrentPage} of {paginationTotalPages}.';

  /**
   * The text to be used when sorting a table column ascending
   */
  @Prop() textSortDirectionAscending: string = 'ascending';

  /**
   * The text to be used when sorting a table column descending
   */
  @Prop() textSortDirectionDescending: string = 'descending';

  /**
   * Will be the aria-label="" attribute value in each sortable <th /> when
   * sorting is enabled.
   *
   * Can be overridden with data-aria-label-ascending and data-aria-label-descending
   * attributes in each <th />
   */
  @Prop() textThAriaLabelSortAscending: string
    = '{innerHTML} currently sorting {textSortDirectionAscending}';

  /**
   * Same as textThAriaLabelSortAscending for sort descending
   */
  @Prop() textThAriaLabelSortDescending: string
    = '{innerHTML} currently sorting {textSortDirectionDescending}';

  /**
   * Passed on to each header panel
   * Text used in header panel when the table is being sorted by any column value ascending - from
   * smallest to largest - or from A-Z.
   */
  @Prop() headerPanelTextSortDirectionAscending: string = 'Ascending';

  /**
   * Passed on to each header panel
   * Text used in header panel when the table is being sorted by any column value decending - from
   * largest to smallest - or from Z-A.
   */
  @Prop() headerPanelTextSortDirectionDescending: string = 'Descending';

  /**
   * Passed on to each header panel
   */
  @Prop() headerPanelTextScreenReader: string = 'Sort and filter column {columnHeadingText}';

  /**
   * Passed on to each header panel
   */
  @Prop() headerPanelTextLabelSort: string = 'Sort';

  /**
   * Passed on to each header panel
   */
  @Prop() headerPanelTextLabelFilter: string = 'Filter';

  /**
   * Passed on to each header panel
   */
  @Prop() headerPanelTextLabelClear: string = 'Clear';

  /**
   * The aria-label on the clear sort buttons
   */
  @Prop() headerPanelTextLabelClearSortAriaLabel: string = 'Clear sort';

  /**
   * The aria-label on the clear filter buttons
   */
  @Prop() headerPanelTextLabelClearFilterAriaLabel: string = 'Clear filters';

  /**
   * Passed on to each header panel
   */
  @Prop() headerPanelTextLabelClose: string = 'Close';

  /**
   * When set to true, displays the caption element
   */
  @Prop() showCaption: boolean = false;

  /**
   * Used to indicate current sort direction 'ascending' or 'descending'
   * using the constants
   * SortDirection.ASCENDING
   * and
   * SortDirection.DESCENDING
   * Will be `null` before sorting has occurred.
   */
  @State() sortDirection: SortDirection = null;

  /**
   * The index of the <th /> that is currently being used to sort.
   * Will be `null` before sorting has occurred.
   */
  @State() sortColumnIndex: number | null = null;

  /**
   * The index of the <th /> that was previously sorted
   * Will be `null` until the second time a sort is performed
   */
  @State() sortColumnIndexPrev: number | null = null;

  /**
   * The selected index of the hp6-text-select used for sorting
   * Set in this.updateTextSelectSort()
   */
  @State() textSelectSortSelectedIndex: number;

  /**
   * An array that will be the options childrent of the sorting hp-text-select
   */
  @State() textSelectSortOptionsArray: [];

  /**
   * This prop enables pagination. If left at initial value of 0, pagination is
   * not enabled.
   *
   * The number of items to display per page.

   * If the number of <tr />'s in the table is less than
   * pagination-items-per-page, the table will not paginated.
   */
  @Prop() paginationItemsPerPage: number = 0;

  /**
   * The total number of pages given @Prop() paginationItemsPerPage and the
   * number of rows <tr /> in the <table />
   */
  @State() paginationTotalPages: number = 0;

  /**
   * The current page of <tr />'s being displayed
   */
  @State() paginationCurrentPage: number = 1;

  /**
   * The index+1 of the first <tr /> being displayed when pagination is used
   */
  @State() paginationItemFirst: number = 1;

  /**
   * The index+1 of the last <tr /> being displayed when pagination is used
   */
  @State() paginationItemLast: number = 0;

  /**
   * The number of rows to be displayed after filtering has happened
   */
  @State() paginationTotalItems: number | null = null;

  /**
   * Boolean value if the sorted and filtered results are to be paginated
   */
  @State() paginationIsPaginated: boolean = false;

  /**
   * Set in this.displayTableRows() - the number of rows after filtering
   */
  @State() numberOfRows: number = 0;

  /**
   * Generated using uid() and memoized in getter `get tableTagId()`
   * Will be value of id="" attribute in the <table /> tag if exists.
   * Will be a generated uid() if element-id="" prop was not passed.
   */
  private tableTagIdMemoized: string = null;

  /**
   * An object of HTMLTableElement memoized in getter `get tableElement()`
   */
  private tableElementMemoized: HTMLTableElement;

  /**
   * An array of <th /> elements set and memoized in getter `get tableHeaderElements()`
   */
  private tableHeaderElementArrayMemoized: HTMLTableCellElement[] = null;

  /**
   * This will be only the first <tbody /> element in the table.
   * Multiple <tbody /> tags not supported.
   *
   * Memoized in getter `get tableBodyElement()`
   */
  private tableBodyElementMemoized: HTMLTableSectionElement = null;

  /**
   * An array of <tr /> elements memoized in getter `get tableRowElements()`
   */
  private tableRowElementsMemoized: Element[] = null;

  /**
   * The <table /> HTMLTableElement as it was when the DOM loaded. Used to
   * reset the <table /> back to the state it was in when the DOM loaded.
   * Set in getter `get tableRowElements()`
   */
  private tableRowElementsOriginal: Element[] = null;

  /**
   * An object of HpTableFilterList that describes the filters in place in this
   */
  private filterList: HpTableFilterList;

  /**
   * The host element in this
   */
  @Element() host: HTMLElement;

  @State() initializedTimestamp: number;

  /**
   * A constant to define the value of the first <option /> in the text-select - the option that
   * resets the sorting
   */
  SORT_TEXT_SELECT_FIRST_OPTION_VALUE = 0;

  /**
   * constructor()
   */
  constructor() {
    this.onClickTh = this.onClickTh.bind(this);
    this.onPaginationControlChange = this.onPaginationControlChange.bind(this);
    this.onSelectChangeEventSort = this.onSelectChangeEventSort.bind(this);
    this.onTableAction = this.onTableAction.bind(this);
    this.renderTextSelectSort = this.renderTextSelectSort.bind(this);
    this.resetTable = this.resetTable.bind(this);
    this.filterList = new HpTableFilterList();
  }

  get isInitialized(): boolean {
    return this.host.querySelector('table[data-table-initialized]') !== null;
  }

  /**
   * Decorate the wrapped table.  Called in componentWillLoad() and whenever the
   * table changes via a MutationObserver.
   */
  initialize() {
    this.tableTagIdMemoized = null;
    this.tableElementMemoized = null;
    this.tableHeaderElementArrayMemoized = null;
    this.tableBodyElementMemoized = null;
    this.tableRowElementsMemoized = null;
    this.tableRowElementsOriginal = null;

    this.addA11yAttributes();
    this.initializeSortable();
    this.initializeThButtons();
    this.addOnClickThEventListeners();
    this.initializeFilterable();
    this.displayTableRows();

    this.initializeXsmallView();
    this.tableElement.setAttribute('data-table-initialized', '');
    this.initializedTimestamp = Date.now();
  }

  /**
   * Stencil.js lifecycle method
   *
   * @returns void
   */
  componentWillLoad(): void {
    this.initialize();

    // Use a mutation observer to watch for changes in the child table and reinitialize if it changes.
    // Only run this in the browser, not in server-side rendering (SSR) or during testing.
    if (Build.isBrowser) {
      const observer = new MutationObserver(() => {
        if (this.isInitialized) {
          return;
        }
        this.initialize();
      });

      observer.observe(this.host, { childList: true, subtree: true });
    }

    this.host.addEventListener('hp6Change', this.onPaginationControlChange);
    this.host.addEventListener('hp6TableActionEvent', this.onTableAction);
  }

  /**
   * Stencil.js lifecycle method
   *
   * @returns void
   */
  disconnectedCallback(): void {
    const { tableHeaderElements } = this;

    tableHeaderElements.forEach(th => {
      th.removeEventListener('click', this.onClickTh);
    });

    this.host.removeEventListener('hp6Change', this.onPaginationControlChange);
    this.host.removeEventListener('hp6TableActionEvent', this.onTableAction);
  }

  /**
   * Handle Hp6TableActionEvent events emitted from hp6-table-header-panel
   *
   * @param event Hp6TableActionEvent
   */
  private onTableAction(event: CustomEvent): void {
    event.stopPropagation();

    const { detail } = event;
    const { action, value, columnIndex } = detail.detail;

    const updateTh = () => {
      const { tableHeaderElements } = this;
      const th = tableHeaderElements[columnIndex];
      this.createThButton(th);
    };

    switch (action.toUpperCase()) {
      case Hp6TableActionsType.SORT_CLEAR:
        this.sortColumnIndex = null;
        this.sortDirection = null;
        updateTh();
        break;

      case Hp6TableActionsType.SORT:
        this.sortColumnIndex = columnIndex;
        this.sortDirection = value;
        break;

      case Hp6TableActionsType.FILTER_CLEAR:
        this.filterList.clearColumn(columnIndex);
        break;

      case Hp6TableActionsType.FILTER_ADD:
        this.filterList.add({ columnIndex, filterText: value });
        break;

      case Hp6TableActionsType.FILTER_REMOVE:
        this.filterList.remove({ columnIndex, filterText: value });
        break;

      case Hp6TableActionsType.FILTER_REPLACE:
        this.filterList.clearColumn(columnIndex);
        this.filterList.add({ columnIndex, filterText: value });
        break;

      default:
        break;
    }

    // always reset pagination to page 1 after sorting or filtering
    this.paginationCurrentPage = 1;

    this.displayTableRows();
  }

  /**
   * Add scope="" attributes to <th /> tags if they do not already exist
   * Add <colgroup><col /></colgroup> tags if they do not already exist
   *
   * https://www.w3schools.com/tags/att_scope.asp
   */
  addA11yAttributes(): void {
    const { tableElement, tableHeaderElements, tableRowElements } = this;

    // add scope="col" to <thead><tr><th scope="col"> elements
    tableHeaderElements.forEach(th => {
      const scopeAttribute = th.getAttribute('scope');

      if (!scopeAttribute) {
        th.setAttribute('scope', 'col');
      }
    });

    // add scope="col" to <tbody><tr><th scope="col"> elements
    tableRowElements.forEach(tr => {
      const thInTr = tr.querySelectorAll('th');

      thInTr.forEach(th => {
        const scopeAttribute = th.getAttribute('scope');

        if (!scopeAttribute) {
          th.setAttribute('scope', 'row');
        }
      });
    });

    // add <colgroup><col /> if it doesn't already exist
    const colgroup = tableElement.querySelector('colgroup');

    if (!colgroup) {
      const colgroupNew = document.createElement('colgroup');

      for (let i = 0; i < tableHeaderElements.length; i++) {
        const col = document.createElement('col');
        colgroupNew.appendChild(col);
      }

      tableElement.insertBefore(colgroupNew, tableElement.firstChild);
    }
  }

  /**
   * Add 'click' event listeners to <th />'s
   *
   * @returns void
   */
  private addOnClickThEventListeners(): void {
    const { isSortable, isFilterable } = this;

    if (!isSortable && !isFilterable) {
      return;
    }

    const { tableHeaderElements } = this;

    tableHeaderElements.forEach((th: HTMLTableCellElement) => {
      th.removeEventListener('click', this.onClickTh);
      th.addEventListener('click', this.onClickTh);
    });
  }

  /**
   * Returns boolean if the th Element argument has sorting enabled
   *
   * @param th Element
   * @returns boolean
   */

  private isSortableTh(th: Element): boolean {
    const { isSortable } = this;

    if (!isSortable) {
      return false;
    }

    // any value of the attribute or existence of the attribute will disable
    const value = th.hasAttribute(DataAttributeTh.SORT_DISABLED);

    if (value) {
      return false;
    }

    return true;
  }

  /**
   * Returns boolean if the th Element argument has filtering enabled
   *
   * @param th Element
   * @returns boolean
   */

  private isFilterableTh(th: Element): boolean {
    const { isFilterable } = this;

    if (!isFilterable) {
      return false;
    }

    const json = th.getAttribute(DataAttributeTh.FILTER_VALUES_JSON);

    // null value is default and means filtering is disabled for the column
    if (json === null) {
      return false;
    }

    try {
      // validate if values is valid json
      const values = JSON.parse(json);

      if (!Array.isArray(values)) {
        const message = `<th > attribute '${DataAttributeTh.FILTER_VALUES_JSON}' is not an array`;
        warn(message);
      }
    } catch (error) {
      const { message } = error;
      warn(message);
    }

    return true;
  }

  /**
   * Get the innerHTML from the <th /> or the <button />
   *
   * @param th
   * @returns string
   */

  private getThText(th: Element): string {
    const button = th.querySelector('button');

    let thText: string;

    if (button) {
      const { innerHTML } = button;
      thText = innerHTML;
    } else {
      const { innerHTML } = th;
      thText = innerHTML;
    }

    return thText;
  }

  /**
   * `Create` a <button /> element inside a sortable <th /> and add ariaLabel
   *
   * @param th Element
   */
  private createThButton(th: Element) {
    if (!this.isSortableTh(th) && !this.isFilterableTh(th)) {
      return;
    }

    let button = th.querySelector('button');

    let text = '';

    if (button) {
      text = button.innerHTML;
    } else {
      // if a button does not already exist in the th - create a button in the th
      const { tableTagId } = this;
      const { innerHTML } = th;

      button = document.createElement('button');
      button.setAttribute('type', 'button');
      button.setAttribute('aria-controls', tableTagId);
      button.setAttribute(DataAttributeTh.BUTTON_IDENTIFIER, 'true');
      button.innerHTML = innerHTML;

      th.innerHTML = '';

      th.appendChild(button);

      text = innerHTML;
    }

    // TODO: use data-* attributes in <th /> to override text values
    const {
      sortDirection,
      textSortDirectionAscending,
      textSortDirectionDescending,
      textThAriaLabelSortAscending,
      textThAriaLabelSortDescending,
    } = this;

    if (sortDirection === SortDirection.ASCENDING) {
      text = textSortDirectionAscending;

      if (textThAriaLabelSortAscending) {
        text = textThAriaLabelSortAscending;
      }

      const thDataAriaLabelAscending = th.getAttribute(DataAttributeTh.ARIA_LABEL_ASCENDING);

      if (thDataAriaLabelAscending) {
        text = thDataAriaLabelAscending;
      }
    }

    if (sortDirection === SortDirection.DESCENDING) {
      text = textSortDirectionDescending;

      if (textThAriaLabelSortDescending) {
        text = textThAriaLabelSortDescending;
      }

      const thDataAriaLabelDescending = th.getAttribute(DataAttributeTh.ARIA_LABEL_DESCENDING);

      if (thDataAriaLabelDescending) {
        text = thDataAriaLabelDescending;
      }
    }

    let textAscending = textSortDirectionAscending;
    let textDescending = textSortDirectionDescending;

    if (sortDirection) {
      if (sortDirection === SortDirection.ASCENDING) {
        const dataSortTextAsc = th.getAttribute(DataAttributeTh.TEXT_SORT_ASCENDING);

        if (dataSortTextAsc) {
          textAscending = dataSortTextAsc;
        }
      } else if (sortDirection === SortDirection.DESCENDING) {
        const dataSortTextDesc = th.getAttribute(DataAttributeTh.TEXT_SORT_DESCENDING);

        if (dataSortTextDesc) {
          textDescending = dataSortTextDesc;
        }
      }
    }

    let thText = this.getThText(th);
    thText = thText || text;

    const thIndex = this.getColumnIndex(th);

    const ariaLabel = stringTemplate(text, {
      innerHTML: thText,
      textSortDirectionAscending: textAscending,
      textSortDirectionDescending: textDescending,
      thIndex,
    }).trim();

    button.setAttribute('aria-label', ariaLabel);
  }

  /**
   * Read the <table /> in this <slot /> and perform actions required to make
   * the table filterable
   *
   * @returns void
   */
  private initializeFilterable(): void {
    const { isFilterable } = this;

    if (!isFilterable) {
      return;
    }

    const { tableHeaderElements } = this;

    const existing = this.host.querySelectorAll('hp6-table-header-panel');
    existing.forEach(panel => {
      panel.remove();
    });

    const { tableTagId } = this;

    tableHeaderElements.forEach((th, index) => {
      if (!this.isFilterableTh(th)) {
        return;
      }

      const json = th.getAttribute(DataAttributeTh.FILTER_VALUES_JSON);
      const isSortable = this.isSortableTh(th);
      const multiple = th.getAttribute(DataAttributeTh.FILTER_VALUES_MULTIPLE);
      const thText = this.getThText(th).trim();

      // create a hp6-table-header-panel and insert it into the DOM
      const filterPanel = document.createElement('hp6-table-header-panel');
      filterPanel.setAttribute('filter-values-json', json);
      filterPanel.setAttribute('is-sortable', isSortable.toString());
      filterPanel.setAttribute('column-index', index.toString());
      filterPanel.setAttribute('column-heading-text', thText);
      filterPanel.removeAttribute('filter-values-active-json');
      filterPanel.setAttribute('table-id', tableTagId);

      if (multiple) {
        filterPanel.setAttribute('filter-values-multiple', 'true');
      }

      // text props that relay from this to header panel
      const {
        headerPanelTextScreenReader,
        headerPanelTextLabelSort,
        headerPanelTextLabelFilter,
        headerPanelTextLabelClear,
        headerPanelTextLabelClearFilterAriaLabel,
        headerPanelTextLabelClearSortAriaLabel,
        headerPanelTextLabelClose,
      } = this;

      filterPanel.setAttribute('text-screen-reader', headerPanelTextScreenReader);

      filterPanel.setAttribute('text-label-sort', headerPanelTextLabelSort);
      filterPanel.setAttribute('text-label-filter', headerPanelTextLabelFilter);
      filterPanel.setAttribute('text-label-clear', headerPanelTextLabelClear);
      filterPanel.setAttribute(
        'text-label-clear-sort-aria-label',
        headerPanelTextLabelClearSortAriaLabel,
      );
      filterPanel.setAttribute(
        'text-label-clear-filter-aria-label',
        headerPanelTextLabelClearFilterAriaLabel,
      );
      filterPanel.setAttribute('text-label-close', headerPanelTextLabelClose);

      // ASCENDING
      let textSortAsc: string;

      const dataSortTextAsc = th.getAttribute(DataAttributeTh.TEXT_SORT_ASCENDING);

      if (dataSortTextAsc) {
        textSortAsc = dataSortTextAsc;
      } else {
        const { headerPanelTextSortDirectionAscending } = this;
        textSortAsc = headerPanelTextSortDirectionAscending;
      }

      filterPanel.setAttribute('text-sort-direction-ascending', textSortAsc);

      // DESCENDING
      let textSortDesc: string;

      const dataSortTextDesc = th.getAttribute(DataAttributeTh.TEXT_SORT_DESCENDING);

      if (dataSortTextDesc) {
        textSortDesc = dataSortTextDesc;
      } else {
        const { headerPanelTextSortDirectionDescending } = this;
        textSortDesc = headerPanelTextSortDirectionDescending;
      }

      filterPanel.setAttribute('text-sort-direction-descending', textSortDesc);

      th.appendChild(filterPanel);
    });
  }

  private tableHeaderTextArray = [];

  /**
   * Read the <table /> in this <slot /> and perform actions required to make
   * the table sortable
   *
   * @returns void
   */
  private initializeSortable(): void {
    const { isSortable } = this;

    if (!isSortable) {
      return;
    }

    const { ariaDescribedbyValue, tableElement } = this;

    tableElement.setAttribute('aria-describedby', ariaDescribedbyValue);

    const tableHeaderTextArray = this.tableHeaderElements.map(th => {
      const { innerHTML } = th;
      return innerHTML;
    });

    this.tableHeaderTextArray = tableHeaderTextArray;
  }

  /**
   * Create the <button />'s inside all <th />'s
   *
   * @return void
   */
  private initializeThButtons(): void {
    const { isFilterable, isSortable } = this;

    if (!isSortable && !isFilterable) {
      return;
    }

    const { tableHeaderElements } = this;

    tableHeaderElements.forEach((th: HTMLTableCellElement) => {
      this.createThButton(th);
    });
  }

  /**
   * Called when the hp6-pagination-control emits an event
   *
   * @param event CustomEvent
   */
  private onPaginationControlChange(event: CustomEvent): void {
    const { detail } = event;
    const { value } = detail;

    if (this.paginationCurrentPage !== value) {
      this.paginationCurrentPage = value;
      this.displayTableRows();
    }
  }

  /**
   * Called when the sort hp6-text-select is changed
   *
   * @param event
   * @returns void
   */
  private onSelectChangeEventSort(event: CustomEvent): void {
    const { SORT_TEXT_SELECT_FIRST_OPTION_VALUE } = this;
    const { target } = event;
    const selectedIndex: number = (target as HTMLSelectElement).selectedIndex;

    if (selectedIndex === SORT_TEXT_SELECT_FIRST_OPTION_VALUE) {
      this.paginationCurrentPage = 1;
      this.sortColumnIndex = null;
      this.sortDirection = null;
      this.displayTableRows();
      return;
    }

    // get a NodeList of <option />'s
    const optionsNodeList = this.host.shadowRoot.querySelectorAll('hp6-text-select option');

    const optionsArray = Array.from(optionsNodeList);
    const option = optionsArray[selectedIndex];

    const sortColumnIndex = parseInt(option.getAttribute('data-sort-column-index'), 10);
    const sortDirectionString = option.getAttribute('data-sort-direction');

    let sortDirection: SortDirection;

    if (sortDirectionString === SortDirection.ASCENDING.toString()) {
      sortDirection = SortDirection.ASCENDING;
    } else if (sortDirectionString === SortDirection.DESCENDING.toString()) {
      sortDirection = SortDirection.DESCENDING;
    }

    this.paginationCurrentPage = 1;
    this.sortColumnIndex = sortColumnIndex;
    this.sortDirection = sortDirection;
    this.displayTableRows();
  }

  /**
   * Memoize and return the HTMLTableElement in this <slot />
   *
   * @returns HTMLTableElement
   */
  get tableElement(): HTMLTableElement {
    if (this.tableElementMemoized) {
      return this.tableElementMemoized;
    }

    this.tableElementMemoized = this.host.querySelector('table');
    return this.tableElementMemoized;
  }

  /**
   * Memoize and return an array of <th /> elements
   *
   * @returns HTMLTableCellElement[]
   */
  get tableHeaderElements(): HTMLTableCellElement[] {
    if (this.tableHeaderElementArrayMemoized) {
      return this.tableHeaderElementArrayMemoized;
    }

    const { tableElementMemoized } = this;

    this.tableHeaderElementArrayMemoized = Array.from(
      tableElementMemoized.querySelectorAll('thead th'),
    );

    return this.tableHeaderElementArrayMemoized;
  }

  /**
   * Memoize and return the first <tbody /> element in this.tableElement
   *
   * @returns HTMLTableSectionElement
   */
  get tableBodyElement(): HTMLTableSectionElement {
    if (this.tableBodyElementMemoized) {
      return this.tableBodyElementMemoized;
    }

    const { tableElementMemoized } = this;
    const tbodyArray = tableElementMemoized.querySelectorAll('tbody');
    const tbody = tbodyArray[0];
    this.tableBodyElementMemoized = tbody;
    return this.tableBodyElementMemoized;
  }

  /**
   * Memoize and return an array of <tr /> elements
   *
   * @returns Element[]
   */
  get tableRowElements(): Element[] {
    if (this.tableRowElementsMemoized) {
      return this.tableRowElementsMemoized;
    }

    this.tableRowElementsMemoized = Array.from(this.host.querySelectorAll('tbody tr'));

    this.tableRowElementsOriginal = [...this.tableRowElementsMemoized];
    return this.tableRowElementsMemoized;
  }

  /**
   * Getter for the id attribute in the <table id="" />
   * Returns id="" attribute of the <table /> if it exists or generates a value
   * if id="" does not exist
   *
   * @returns string
   */
  get tableTagId(): string {
    const { tableTagIdMemoized } = this;

    if (tableTagIdMemoized) {
      return tableTagIdMemoized;
    }

    const { tableElement, isFilterable, isSortable } = this;
    let tableTagId = tableElement.getAttribute('id');

    if (!tableTagId && isSortable && isFilterable) {
      tableTagId = `table-id-${uid()}`;

      tableElement.setAttribute('id', tableTagId);
    }

    this.tableTagIdMemoized = tableTagId;

    return tableTagId;
  }

  /**
   * Getter for the id="" attribute of the <hp6-text-select />
   * component used for pagination.
   *
   * @returns string
   */
  get textSelectElementId() {
    const { tableTagId } = this;
    return `${tableTagId}_text-select`;
  }

  /**
   * Getter for the id="" attribute of the <hp6-pagination-control />
   * component used for pagination.
   *
   * @returns string
   */
  get paginationControlElementId() {
    const { tableTagId } = this;
    return `${tableTagId}-pagination-control`;
  }

  /**
   * The value used in the aria-describedby="" attribute of the table
   */
  get ariaDescribedbyValue() {
    const { tableTagId } = this;
    return `${tableTagId}_info`;
  }

  /**
   * Remove aria attributes and css classes from all <th /> in <thead />
   */
  private resetThead(): void {
    const { tableHeaderElements } = this;

    tableHeaderElements.forEach(th => {
      th.removeAttribute('aria-sort');

      const isSortableTh = this.isSortableTh(th);

      if (isSortableTh) {
        th.classList.remove(ThSortClass.ASCENDING);
        th.classList.remove(ThSortClass.DESCENDING);
      }
    });
  }

  /**
   * Arg is an Event object target. Returns the <th /> that was clicked
   * during Event
   *
   * @param target HTMLElement
   * @returns HTMLTableCellElement
   */

  private getClickedTh(target: HTMLElement): HTMLTableCellElement {
    let th = target as HTMLTableCellElement;
    let current = target as HTMLTableCellElement;

    // target may be a child node of the <th />
    // keep getting parent node until the <th /> is found
    while (current.tagName !== 'TH') {
      // @ts-expect-error Type 'ParentNode' is missing the following properties from type
      // 'HTMLTableCellElement': abbr, align, axis, bgColor, and 233 more.ts(2740)
      current = current.parentNode;
    }

    th = current;

    return th;
  }

  /**
   * Get the column index of th
   *
   * @param th Element
   * @returns number
   */

  private getColumnIndex(th: Element) {
    return Array.from(th.parentNode.children).indexOf(th);
  }

  /**
   * Used thusly th.addEventListener('click', this.onClickTh);
   * Set all @State variables in this method, and read them later
   * Clicking a <th /> will perform a sort or enable a filter. Pagination
   * happens elsewhere.
   *
   * @param event
   * @return void
   */
  private onClickTh(event: Event): void {
    event.stopPropagation();

    const target = event.target as HTMLElement;

    // ignore everything that is not a click on the <th><button></button></th>
    if (target.tagName.toLocaleLowerCase() !== 'button') {
      if (!target.getAttribute(DataAttributeTh.BUTTON_IDENTIFIER)) {
        return;
      }
    }

    const th = this.getClickedTh(target);
    const clickedColumnIndex = this.getColumnIndex(th);

    const isFilterable = this.isFilterableTh(th);
    const { isSortable } = this;

    if (isFilterable) {
      const headerElement = target.parentElement.querySelector('hp6-table-header-panel');

      if (headerElement) {
        const hasAttribute = headerElement.hasAttribute('is-visible');

        if (hasAttribute) {
          target.parentElement
            .querySelector('hp6-table-header-panel')
            .removeAttribute('is-visible');
        } else {
          target.parentElement
            .querySelector('hp6-table-header-panel')
            .setAttribute('is-visible', 'true');
        }

        return;
      }
    }

    if (!isSortable) {
      return;
    }

    // setting the pagination to the first page when sorting avoids all sorts
    // of confusion
    this.paginationCurrentPage = 1;

    const { sortDirection, sortColumnIndex } = this;

    let sortDirectionNew = SortDirection.ASCENDING;

    if (sortDirection) {
      if (sortColumnIndex === clickedColumnIndex) {
        if (sortDirection === SortDirection.ASCENDING) {
          sortDirectionNew = SortDirection.DESCENDING;
        }
      }
    }

    this.sortColumnIndexPrev = sortColumnIndex;
    this.sortColumnIndex = clickedColumnIndex;
    this.sortDirection = sortDirectionNew;

    this.displayTableRows();
  }

  /**
   * Check the correct radios and checkboxes in all the table-header-panel's
   * in all the <th />'s
   */
  private resetTableHeaderPanels(): void {
    const { sortColumnIndex, sortDirection } = this;

    const headerArray = Array.from(this.host.querySelectorAll('hp6-table-header-panel'));

    // remove the sort-direction prop from all table header panels
    headerArray.forEach(header => {
      header.removeAttribute('sort-direction');

      if (sortColumnIndex !== null && sortDirection !== null) {
        if (header.getAttribute('column-index') === sortColumnIndex.toString()) {
          header.setAttribute('sort-direction', sortDirection);
        }
      }
    });
  }

  /**
   * Sort, filter, paginate, and display the <tr />'s
   * Called after any of the following occur:
   *  - component has loaded
   *  - a <th /> is clicked
   *  - pagination is changed
   *  - sorting has changed
   *  - filtering has changed
   */
  private displayTableRows(): void {
    this.resetThead();
    this.resetTableHeaderPanels();

    // always start with all table rows
    const { tableRowElementsOriginal } = this;

    let rows = [...tableRowElementsOriginal];
    rows = this.filter(rows);

    this.numberOfRows = rows.length;

    rows = this.sort(rows);
    rows = this.paginate(rows);

    // remove all existing rows in tbody
    const { tableBodyElement } = this;
    while (tableBodyElement.firstChild) {
      tableBodyElement.removeChild(tableBodyElement.firstChild);
    }

    // replace <tr />'s in <tbody />
    rows.forEach(tr => tableBodyElement.appendChild(tr));

    this.updateAllTh();
  }

  /**
   * Filter the <tr />'s in table after a <th /> has been clicked
   *
   * Filter rules:
   * between columns - filter is AND
   * between rows/same column - filter is OR - if checkboxes/multi filter
   *
   * @param rowElements Array<Element>
   * @return Array<Element>
   */
  private filter(rowElements: Element[]): Element[] {
    const { isFilterable } = this;

    if (!isFilterable) {
      return rowElements;
    }

    if (this.filterList.isEmpty()) {
      return rowElements;
    }

    const filteredRows = [];

    rowElements.forEach((row: Element) => {
      const cells = row.querySelectorAll('th, td');

      const rowTextArray = [];

      cells.forEach((cell: Element) => {
        const span = cell.querySelector('span.cellcontent');

        if (span) {
          const { innerHTML } = span;
          rowTextArray.push(innerHTML);
        }
      });

      if (this.filterList.rowMatch(rowTextArray)) {
        filteredRows.push(row);
      }
    });

    return filteredRows;
  }

  /**
   * Set classes in all <th /> elements after a change
   *
   * @returns void
   */
  private updateAllTh(): void {
    const { isFilterable, sortColumnIndex, sortDirection, tableHeaderElements, isSortable } = this;

    if (isFilterable) {
      tableHeaderElements.forEach((th: Element, index: number) => {
        if (this.isFilterableTh(th)) {
          th.classList.remove('active');

          if (this.filterList.containsColumnIndex(index) || sortColumnIndex === index) {
            th.classList.add('active');
          }

          const panel = th.querySelector('hp6-table-header-panel');
          panel.removeAttribute('filter-values-active-json');

          const filterValuesActiveArray = this.filterList.getColumnFiltersActive(index);

          if (filterValuesActiveArray) {
            const filterValuesActiveArrayJson = JSON.stringify(filterValuesActiveArray);

            panel.setAttribute('filter-values-active-json', filterValuesActiveArrayJson);
          }
        }
      });
    }

    if (sortColumnIndex !== null) {
      const th = tableHeaderElements[sortColumnIndex];

      th.classList.add(
        sortDirection === SortDirection.ASCENDING ? ThSortClass.ASCENDING : ThSortClass.DESCENDING,
      );

      if (isSortable) {
        this.createThButton(th);
      }

      const ariaSortValue = sortDirection === SortDirection.ASCENDING ? 'ascending' : 'descending';

      th.setAttribute('aria-sort', ariaSortValue);
    }
  }

  /**
   * Sort rowElements and return sorted rowElements
   *
   * @param rowElements Array<Element>
   * @return Array<Element>
   */
  private sort(rowElements: Element[]): Element[] {
    const { sortColumnIndex } = this;

    if (sortColumnIndex === null) {
      return rowElements;
    }

    const { tableHeaderElements, sortDirection } = this;

    const th = tableHeaderElements[sortColumnIndex];
    const isAscending = sortDirection === SortDirection.ASCENDING;

    const sortType = th.getAttribute(DataAttributeTh.SORT_TYPE) || SortType.DEFAULT;

    const getCellValue = (tr, indexCell) => {
      const td = tr.children[indexCell];
      const dataSortValue = td.getAttribute(DataAttributeTh.SORT_VALUE);

      if (dataSortValue) {
        return dataSortValue;
      }

      return td.innerText || td.textContent;
    };

    const compareFnAlphabetical
      = (indexCell: number, asc: boolean) =>
        (a: HTMLTableRowElement, b: HTMLTableRowElement): number =>
          ((value1, value2) =>
            value1 !== ''
            && value2 !== ''
            && !Number.isNaN(Number(value1))
            && !Number.isNaN(Number(value2))
              ? value1 - value2
              : value1.toString().localeCompare(value2))(
            getCellValue(asc ? a : b, indexCell),
            getCellValue(asc ? b : a, indexCell),
          );

    const compareFnNumeric = compareFnAlphabetical;

    // default sort type is SortType.ALPHABETICAL
    let compareFn = compareFnAlphabetical;

    if (sortType === SortType.ALPHABETICAL) {
      compareFn = compareFnAlphabetical;
    } else if (sortType === SortType.NUMERIC) {
      compareFn = compareFnNumeric;
    } else {
      let message = `sortColumnIndex ${sortColumnIndex} has unrecognized `;
      message += `value in '${DataAttributeTh.SORT_TYPE}' attribute '${sortType}' - `;
      message += `using default sort type value ${SortType.DEFAULT}`;
      warn(message);
    }

    const indexOf = this.getColumnIndex(th);

    const sortedRows = rowElements.sort(compareFn(indexOf, isAscending));
    return sortedRows;
  }

  /**
   * Returns this.paginationCurrentPage of arg rowElements
   *
   * @param rowElements Array<Element>
   * @returns Array<Element>
   */
  private paginate(rowElements: Element[]): Element[] {
    const { paginationItemsPerPage } = this;

    if (!paginationItemsPerPage) {
      return rowElements;
    }

    if (rowElements.length <= paginationItemsPerPage) {
      // update @State() variables
      this.paginationIsPaginated = false;
      this.paginationItemFirst = 1;
      this.paginationItemLast = rowElements.length;
      this.paginationTotalPages = 1;

      return rowElements;
    }

    let paginationItemFirst = 1;

    const { paginationCurrentPage } = this;

    if (paginationCurrentPage > 1) {
      paginationItemFirst
        = paginationItemsPerPage * paginationCurrentPage - paginationItemsPerPage + 1;
    }

    let paginationItemLast = paginationItemFirst + paginationItemsPerPage - 1;

    const rowElementsNext = rowElements.slice(paginationItemFirst - 1, paginationItemLast);

    const numberOfRows = rowElements.length;

    const paginationTotalPages = Math.ceil(numberOfRows / paginationItemsPerPage);

    if (paginationItemLast > numberOfRows) {
      paginationItemLast = numberOfRows;
    }

    // update @State() variables
    this.paginationIsPaginated = true;
    this.paginationItemFirst = paginationItemFirst;
    this.paginationItemLast = paginationItemLast;
    this.paginationTotalPages = paginationTotalPages;

    return rowElementsNext;
  }

  /**
   * Reset the table back to the way it was in the DOM when the page was
   * initially loaded
   *
   * @return Promise<void>
   */
  @Method()
  async resetTable(): Promise<void> {
    this.paginationCurrentPage = 1;
    this.textSelectSortSelectedIndex = this.SORT_TEXT_SELECT_FIRST_OPTION_VALUE;
    this.sortColumnIndex = null;
    this.sortDirection = null;
    this.numberOfRows = this.tableRowElementsOriginal.length;

    this.filterList.reset();

    this.resetThead();
    this.resetTableHeaderPanels();

    this.initializeFilterable();
    this.updateAllTh();

    const { tableBodyElement, tableRowElementsOriginal, paginationItemsPerPage } = this;

    let rows = tableRowElementsOriginal;

    if (paginationItemsPerPage) {
      rows = this.paginate(rows);
    }

    // remove all existing rows in tbody
    while (tableBodyElement.firstChild) {
      tableBodyElement.removeChild(tableBodyElement.firstChild);
    }

    // replace <tr />'s in <tbody />
    rows.forEach(tr => tableBodyElement.appendChild(tr));
  }

  /**
   * Do the things required to make the table rows stack at xsmall breakpoint
   * Create and store a data structure to stack the table rows at xs breakpoint
   *
   * @returns void
   */
  private initializeXsmallView() {
    const { tableHeaderElements, tableRowElements } = this;
    const theadHeaders = [].slice.call(tableHeaderElements);

    [].slice.call(tableRowElements).forEach(tr => {
      [].slice.call(tr.querySelectorAll('th, td')).forEach((element, j) => {
        element.setAttribute('data-row-header', theadHeaders[j].textContent.trim());
        // wrap the cell contents in a <span>
        const contentSpan = document.createElement('span');
        contentSpan.classList.add('cellcontent');
        contentSpan.innerHTML = element.innerHTML;

        element.innerHTML = null;
        element.appendChild(contentSpan);
      });

      [].slice.call(tr.querySelectorAll('th')).forEach((element, k) => {
        element.setAttribute('data-row-column-header', theadHeaders[k].textContent);
      });
    });
  }

  /**
   * Render the hp6-text-select in the table top - used for sorting when in xsmall view
   *
   * @returns tsx
   */
  private renderTextSelectSort() {
    const optionsArray = [];
    const { textSelectFirstOptionLabel, textSortDirectionAscending, textSortDirectionDescending }
      = this;

    const optionOne = {
      value: textSelectFirstOptionLabel,
      label: textSelectFirstOptionLabel,
      optionData: {
        isOptionOne: true,
      },
    };

    optionsArray.push(optionOne);

    let count = 0;

    const { tableHeaderTextArray } = this;

    const { tableHeaderElements } = this;

    for (let i = 0; i < tableHeaderElements.length; i++) {
      const th = tableHeaderElements[i];
      const tableHeaderText = tableHeaderTextArray[i];

      if (!this.isSortableTh(th)) {
        return null;
      }

      const thTextSelectabelAscending = th.getAttribute(
        DataAttributeTh.TEXT_SELECT_LABEL_ASCENDING,
      );

      const thTextSelectabelDescending = th.getAttribute(
        DataAttributeTh.TEXT_SELECT_LABEL_DESCENDING,
      );

      const labelAscending
        = thTextSelectabelAscending || `${tableHeaderText} - ${textSortDirectionAscending}`;

      const labelDescending
        = thTextSelectabelDescending || `${tableHeaderText} - ${textSortDirectionDescending}`;

      const optionAscending = {
        value: count,
        label: labelAscending,
        sortColumnIndex: i,
        sortDirection: SortDirection.ASCENDING,
      };

      count += 1;

      const optionDescending = {
        value: count,
        label: labelDescending,
        sortColumnIndex: i,
        sortDirection: SortDirection.DESCENDING,
      };

      count += 1;

      optionsArray.push(optionAscending);
      optionsArray.push(optionDescending);
    }

    const { tableTagId, textSelectElementId, textSelectLabel, textSelectSortSelectedIndex } = this;

    return (
      <hp6-text-select
        ally-controls={tableTagId}
        selected-index={textSelectSortSelectedIndex}
        label-text={textSelectLabel}
      >
        <select id={textSelectElementId} onChange={this.onSelectChangeEventSort}>
          {optionsArray.map(option => {
            const { value, label, sortColumnIndex, sortDirection } = option;

            return (
              <option
                data-sort-column-index={sortColumnIndex}
                data-sort-direction={sortDirection}
                value={value}
              >
                {label}
              </option>
            );
          })}
        </select>
      </hp6-text-select>
    );
  }

  /**
   * Render the top of a sorted/paginated table
   *
   * @returns Fragment
   */
  private renderTableTop() {
    const { paginationIsPaginated, isSortable } = this;

    if (!paginationIsPaginated && !isSortable) {
      return null;
    }

    const {
      ariaDescribedbyValue,
      numberOfRows,
      sortColumnIndex,
      sortDirection,
      textSortDirectionAscending,
      textSortDirectionDescending,
    } = this;

    const numberOfRowsString = numberOfRows.toLocaleString('en-US');

    let textSortDirection = '';

    if (sortDirection === SortDirection.ASCENDING) {
      textSortDirection = textSortDirectionAscending;
    } else if (sortDirection === SortDirection.DESCENDING) {
      textSortDirection = textSortDirectionDescending;
    }

    const tableDescriptionDivAttributes = {
      'aria-hidden': 'true',
    };

    const tableDescriptionDivScreenReaderAttributes = {
      'id': ariaDescribedbyValue,
      'aria-live': 'polite',
      'role': 'status',
    };

    let tableDescriptionText = '';
    let screenReaderOnlyDescriptionText = '';
    let screenReaderCurrentPageText = '';

    if (this.paginationIsPaginated) {
      const {
        textTableDescriptionPagination,
        paginationCurrentPage,
        paginationTotalPages,
        paginationItemFirst,
        paginationItemLast,
        textTableDescriptionPaginationScreenReader,
        textPaginationCurrentPageScreenReader,
      } = this;

      tableDescriptionText = stringTemplate(textTableDescriptionPagination, {
        numberOfRows: numberOfRowsString,
        paginationItemFirst,
        paginationItemLast,
      });
      tableDescriptionText = `${tableDescriptionText}`;

      screenReaderOnlyDescriptionText = stringTemplate(textTableDescriptionPaginationScreenReader, {
        numberOfRows: numberOfRowsString,
        paginationItemFirst,
        paginationItemLast,
      });

      screenReaderCurrentPageText = stringTemplate(textPaginationCurrentPageScreenReader, {
        paginationCurrentPage,
        paginationTotalPages,
      });
    } else {
      const { textTableDescription } = this;
      tableDescriptionText = stringTemplate(textTableDescription, {
        numberOfRows: numberOfRowsString,
      });

      // screen reader only reads different text if the table is paginated
      screenReaderOnlyDescriptionText = tableDescriptionText;
    }

    let screenReaderOnlySortText = '';

    if (sortColumnIndex !== null) {
      const { textTableSortingScreenReader } = this;
      const th = this.tableHeaderElements[sortColumnIndex];
      const button = th.querySelector('button');
      const { innerHTML } = button;

      screenReaderOnlySortText = stringTemplate(textTableSortingScreenReader, {
        innerHTML,
        sortColumnNumber: sortColumnIndex + 1,
        textSortDirection,
      });
    }

    let resetButtonClass = '';

    if (sortColumnIndex !== null || !this.filterList.isEmpty()) {
      resetButtonClass = 'visible';
    }

    const { textLabelResetButton } = this;

    return (
      <div class="hp-table-top">
        <hp6-screen-reader-only>
          <span {...tableDescriptionDivScreenReaderAttributes}>
            {screenReaderOnlySortText}
            {' '}
            {screenReaderCurrentPageText}
            {' '}
            {screenReaderOnlyDescriptionText}
          </span>
        </hp6-screen-reader-only>
        <div class="hp-table-description" {...tableDescriptionDivAttributes}>
          {tableDescriptionText}
        </div>
        <div />
        <div class="hp-table-text-select-container">
          {isSortable && this.renderTextSelectSort()}
        </div>
        <div class={`hp-table-reset-button-container ${resetButtonClass}`}>
          <hp6-button-decorator type-selection="tertiary">
            <button
              type="button"
              onClick={() => {
                this.resetTable();
              }}
            >
              <hp6-icon name="system-delete" size-selection="sm" />
              {textLabelResetButton}
            </button>
          </hp6-button-decorator>
        </div>
      </div>
    );
  }

  /**
   * render()
   *
   * @returns tsx
   */
  render() {
    const { showCaption, tableTagId, paginationControlElementId, paginationTotalPages, paginationCurrentPage, paginationIsPaginated } = this;

    // Note that the topper and pagination control must be in light DOM to allow aria attributes to work correctly with the table in the slot
    return (
      <Host class={`${showCaption ? '' : 'hide-caption'}`}>
        <div class="hp-table-top-container">{this.renderTableTop()}</div>
        <slot />
        {paginationIsPaginated ? (
          <hp6-pagination-control
            id={paginationControlElementId}
            ally-controls={tableTagId}
            page-count={paginationTotalPages.toString()}
            page-current={paginationCurrentPage.toString()}
          />
        ) : null}
      </Host>
    );
  }
}
